import {
  Box,
  FormControl,
  FormLabel,
  Stack,
  Text,
  Flex,
  Select,
} from "@chakra-ui/react";
import { Cloud, AlertCircle } from "lucide-react";
import React, { useCallback, useMemo } from "react";
import { observer } from "mobx-react-lite";
import { MonitoredLocationPreview } from "pages/Alert/components/MonitoredLocationPreview";
import { InsightSelector } from "pages/Alert/components/InsightSelector";
import {
  ILocationMenuStation,
  ILocationMenuInsight,
  ILocationMenuInsightCondition,
} from "stores/data/operationRuleLocations/OperationRuleLocationModel";
import { LatLngLiteral } from "leaflet";
import { useStores } from "stores/setup/use-store";
import { StoreState } from "enum/StoreState";
import {
  ReadOnlyConditionBuilder,
  EnvironmentalVariablesLoadingSkeleton,
  EnvironmentalVariablesErrorState,
} from "components/ConditionBuilder/components";
import { RestrictionTypeDisplay } from "pages/Alert/components/RestrictionTypeDisplay";

interface InsightAlertEditSectionProps {
  dashboardId?: number;
  dashboardName: string;
  insightId?: number;
  insightName: string;
  conditions: ILocationMenuInsightCondition[];
  locationName: string;
  station?: ILocationMenuStation;
  geofence?: LatLngLiteral[];
  t: (key: string) => string;
  onDashboardChange?: (dashboardId: number | undefined) => void;
  onInsightChange?: (insight: ILocationMenuInsight, locationId: number) => void;
}

export const InsightAlertEditSection: React.FC<InsightAlertEditSectionProps> =
  observer(
    ({
      dashboardId,
      dashboardName,
      insightId,
      insightName,
      conditions,
      locationName,
      station,
      geofence,
      t,
      onDashboardChange,
      onInsightChange,
    }) => {
      const {
        dataStores: { operationRuleStore, insightAlertStore },
      } = useStores();

      const dashboards = operationRuleStore.operationRules;
      const isDashboardsLoading =
        operationRuleStore.state === StoreState.PEDDING;

      const locationMenuList = insightAlertStore.locationMenuList;
      const isInsightsLoading =
        insightAlertStore.locationMenuListState === StoreState.PEDDING;
      const selectedInsight = insightAlertStore.selectedInsight;

      // Convert conditions to format expected by ReadOnlyConditionBuilder
      const convertToReadOnlyFormat = useCallback(
        (rawConditions: ILocationMenuInsightCondition[]) => {
          if (!rawConditions || rawConditions.length === 0) return [];

          return rawConditions.map((group, index) => ({
            id: `group-${index}`,
            conditions: group.conditions.map((cond) => ({
              environmentalVariable: cond.environmentalVariable,
              environmentalVariableId: cond.environmentalVariableId,
              environmentalVariableName: cond.environmentalVariableName,
              comparisonOperator: cond.comparisonOperator,
              value: cond.value,
            })),
            groupOperator: (group.groupOperator || "and").toLowerCase(),
            conditionOperator: (group.conditionOperator || "and").toLowerCase(),
          }));
        },
        [],
      );

      const displayConditions = useMemo(() => {
        if (selectedInsight?.conditions) {
          return convertToReadOnlyFormat(selectedInsight.conditions);
        }
        return convertToReadOnlyFormat(conditions);
      }, [selectedInsight, conditions, convertToReadOnlyFormat]);

      const hasConditions = displayConditions.length > 0;

      const currentLocationData = useMemo(() => {
        if (selectedInsight && insightAlertStore.selectedInsightLocationId) {
          const location = locationMenuList.find(
            (loc) => loc.id === insightAlertStore.selectedInsightLocationId,
          );
          if (location) {
            return {
              name: location.name,
              station: location.station,
              geofence: location.geofence,
            };
          }
        }
        // Fall back to original alert data
        return {
          name: locationName,
          station,
          geofence,
        };
      }, [
        selectedInsight,
        insightAlertStore.selectedInsightLocationId,
        locationMenuList,
        locationName,
        station,
        geofence,
      ]);

      // Handle dashboard change
      const handleDashboardChange = useCallback(
        (e: React.ChangeEvent<HTMLSelectElement>) => {
          const newDashboardId = e.target.value
            ? Number(e.target.value)
            : undefined;

          // Clear insight selection when dashboard changes
          insightAlertStore.setSelectedInsight(null);
          insightAlertStore.setSelectedInsightLocationId(null);

          // Fetch insights for selected dashboard
          if (newDashboardId) {
            insightAlertStore.getLocationMenuListAPI(newDashboardId);
          } else {
            insightAlertStore.setLocationMenuList([]);
          }

          onDashboardChange?.(newDashboardId);
        },
        [insightAlertStore, onDashboardChange],
      );

      // Handle insight selection
      const handleInsightSelect = useCallback(
        (insight: ILocationMenuInsight, locationId: number) => {
          insightAlertStore.setSelectedInsight(insight);
          insightAlertStore.setSelectedInsightLocationId(locationId);
          onInsightChange?.(insight, locationId);
        },
        [insightAlertStore, onInsightChange],
      );

      // Get current selected dashboard ID (from prop or store)
      const currentDashboardId = useMemo(() => {
        return insightAlertStore.selectedDashboardIdForEdit ?? dashboardId;
      }, [insightAlertStore.selectedDashboardIdForEdit, dashboardId]);

      // Check if dashboard was changed from original
      const dashboardWasChanged = useMemo(() => {
        return (
          insightAlertStore.selectedDashboardIdForEdit !== null &&
          insightAlertStore.selectedDashboardIdForEdit !== dashboardId
        );
      }, [insightAlertStore.selectedDashboardIdForEdit, dashboardId]);

      // Get current selected insight ID
      // If dashboard was changed and no new insight selected, don't show the old insight
      const currentInsightId = useMemo(() => {
        if (dashboardWasChanged && !selectedInsight) {
          return undefined;
        }
        return selectedInsight?.id ?? insightId;
      }, [selectedInsight, insightId, dashboardWasChanged]);

      // Get restriction type from the currently selected or original insight
      const currentRestrictionType = useMemo((): boolean | undefined => {
        // If user selected a new insight, use its restrictionType
        if (selectedInsight) {
          return selectedInsight.restrictionType;
        }
        // If dashboard was changed but no insight selected yet, don't show
        if (dashboardWasChanged) {
          return undefined;
        }
        // Find the original insight in the location menu list
        if (insightId && locationMenuList.length > 0) {
          for (const location of locationMenuList) {
            const insight = location.items.find((i) => i.id === insightId);
            if (insight) {
              return insight.restrictionType;
            }
          }
        }
        return undefined;
      }, [selectedInsight, dashboardWasChanged, insightId, locationMenuList]);

      return (
        <Stack spacing={6}>
          {/* Select an Insight Section */}
          <Box
            bg="white"
            border="1px solid"
            borderColor="#D4D4D8"
            borderRadius="8px"
            p={4}
          >
            {/* Section Header */}
            <Flex align="center" gap="16px" mb={4}>
              <Cloud size={20} color="#086AA9" />
              <Text fontSize="16px" color="#086AA9" fontWeight="400">
                {t("selectAnInsight")}
              </Text>
            </Flex>

            {/* Selectors + Restriction Type - Side by side */}
            <Flex gap="16px" align="start">
              {/* Dashboard Selector */}
              <Box flex="1">
                <FormControl>
                  <FormLabel color="#414651" fontWeight="500" fontSize="14px">
                    {t("selectDashboard")}
                  </FormLabel>
                  <Select
                    placeholder={t("selectDashboardPlaceholder")}
                    value={currentDashboardId || ""}
                    onChange={handleDashboardChange}
                    isDisabled={isDashboardsLoading}
                    borderColor="#D5D7DA"
                    _hover={{ borderColor: "#A1A1AA" }}
                    _focus={{
                      borderColor: "#0884CC",
                      boxShadow: "0 0 0 1px #0884CC",
                    }}
                    h="44px"
                    fontSize="16px"
                    color={currentDashboardId ? "#414651" : "#717680"}
                  >
                    {dashboards.map((dashboard) => (
                      <option key={dashboard.id} value={dashboard.id}>
                        {dashboard.name}
                      </option>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              {/* Insight Selector */}
              <Box flex="1">
                <FormControl>
                  <FormLabel color="#414651" fontWeight="500" fontSize="14px">
                    {t("selectInsight")}
                  </FormLabel>
                  <InsightSelector
                    locations={locationMenuList}
                    selectedInsightId={currentInsightId}
                    onSelect={handleInsightSelect}
                    placeholder={
                      dashboardWasChanged
                        ? t("selectInsightPlaceholder")
                        : insightName || t("selectInsightPlaceholder")
                    }
                    searchPlaceholder={t("searchInsightPlaceholder")}
                    isLoading={isInsightsLoading}
                    isDisabled={!currentDashboardId}
                    noResultsText={t("noInsightsFound")}
                    noInsightsInLocationText={t("noInsightsInLocation")}
                  />
                </FormControl>
              </Box>

              {/* Restriction Type Display - inline with selectors */}
              <Box flexShrink={0}>
                <RestrictionTypeDisplay
                  restrictionType={currentRestrictionType}
                  t={t}
                />
              </Box>
            </Flex>
          </Box>

          {/* Monitored Location Preview - Now above Alert Type and Conditions */}
          {(currentLocationData.station ||
            (currentLocationData.geofence &&
              currentLocationData.geofence.length > 0)) && (
            <MonitoredLocationPreview
              locationName={currentLocationData.name}
              station={currentLocationData.station}
              geofence={currentLocationData.geofence}
              t={t}
            />
          )}

          {/* Conditions Section */}
          <Box>
            <Text fontSize="14px" fontWeight="500" color="#414651" mb={3}>
              {t("selectedInsightConditions")}
            </Text>

            {insightAlertStore.isLoadingEnvironmentalVariables ? (
              <EnvironmentalVariablesLoadingSkeleton />
            ) : insightAlertStore.hasErrorLoadingEnvironmentalVariables ? (
              <EnvironmentalVariablesErrorState
                errorMessage={t("errorLoadingEnvironmentalVariables")}
                retryLabel={t("retryLoadEnvVariables")}
                onRetry={() =>
                  insightAlertStore.retryLoadEnvironmentalVariables()
                }
              />
            ) : hasConditions ? (
              <Box bg="#f4f4f5" borderRadius={8} p={6}>
                <ReadOnlyConditionBuilder
                  conditionGroups={displayConditions}
                  environmentalVariables={
                    insightAlertStore.environmentalVariables
                  }
                  t={t}
                />
              </Box>
            ) : (
              <Flex
                direction="column"
                align="center"
                justify="center"
                py={8}
                color="gray.500"
              >
                <AlertCircle size={32} color="#A0AEC0" />
                <Text
                  fontSize="sm"
                  mt={3}
                  textAlign="center"
                  fontStyle="italic"
                >
                  {t("noConditionsAvailable")}
                </Text>
              </Flex>
            )}
          </Box>
        </Stack>
      );
    },
  );

export default InsightAlertEditSection;
